import React from 'react';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { DownOutlined } from '@ant-design/icons';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Row,
  Col,
  Tabs,
  Menu,
  Dropdown,
  Table,
  Empty,
  Descriptions,
  Divider,
  message,
  Tag,
  Alert,
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from 'moment';
import { Client } from '../../clients/types';
import { clientSearchFunction } from '../../../utils/search';
import CreateContact from './CreateContact/CreateContact';
import CreateItinerary from '../CreateItinerary/CreateItinerary';
import { BudgetConfig, Contact } from '../types';
import { capitalizeFirstChar } from '../../../utils/string';
import UUID from '../../../utils/uuid';
import { ICreateContactFormValues } from './CreateContact/CreateContactForm';
import { ItineraryCandidate } from '../CreateItinerary/CreateItineraryForm';
import {
  computeItineraryTotalWithoutVAT,
  computeItineraryTotalWithVAT,
  computeItineraryVATSumatory,
} from '../utils';
import { formatDate, formatTime } from '../../../utils/date';
import EditItinerary from '../EditItinerary/EditItinerary';
import StickyFooterFormButtons from '../../StickyFooterFormButtons/StickyFooterFormButtons';
import { sanitizeObject } from '../../../utils/object';
import { MainContext } from '../../../contexts/mainContext';
import {
  ContactsDocument,
  ContactsQuery,
  ContactsQueryVariables,
  useCreateContactMutation,
  useGetClientTemplateQuery,
  VehicleTypesQuery,
} from '../../../graphql/datamodel.gen';

import './create-budget-form.scss';
import { getClient } from '../../../apollo';
import { gql } from '@apollo/client';
import BasicHTMLEditor from '../../BasicHTMLEditor';
import { JSONContent } from '@tiptap/react';

interface ICreateBudgetFormProps extends FormComponentProps {
  draft: boolean;
  budget?: BudgetInstance;
  refetchBudgets: () => any;
  refetchContacts: () => any;
  refetchClients: () => any;
  allClients: Client[];
  allContacts: Contact[];
  allVehicleTypes: VehicleTypesQuery['vehicleTypes'];
  budgetConfig?: BudgetConfig;
  onSubmit: (budget: BudgetCandidate) => Promise<any>;
  onConfirmItinerary: (itineraryId: string) => Promise<any>;
  close: () => any;
}

interface ICreateBudgetFormState {
  confirmItineraryLoading: boolean;
  createNewContactDrawerVisible: boolean;
  createNewItineraryDrawerVisible: boolean;
  editItineraryDrawerVisible: boolean;
  itineraries: ItineraryCandidate[];
  selectedItineraries: string[];
  contacts: TemporaryContact[];
  selectedContact: string;
  activeTemplate: string;
  selectedClientId: string;
}

interface ICreateBudgetFormValues {
  creationDate: moment.Moment;
  validityPeriod: moment.Moment[];
  client: string;
  status: string;
  company: string;
  reference?: string;
  contact?: string;
  metThrough?: string;
  requestSource?: string;
  templateHeading?: string;
  templateBody?: string;
}

export interface BudgetInstance
  extends Omit<ICreateBudgetFormValues, 'contact' | 'creationDate'> {
  id: string;
  readableId: string;
  itineraries: ItineraryCandidate[];
  contact?: Contact;
  creationDate: moment.Moment;
}

export type BudgetCandidate = Omit<
  BudgetInstance,
  'id' | 'readableId' | 'contact'
> & {
  contactId?: string;
};

interface TemporaryContact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

const CreateBudgetForm: React.FC<ICreateBudgetFormProps> = (
  props: ICreateBudgetFormProps,
) => {
  const context = React.useContext(MainContext);
  const [confirmItineraryLoading, setConfirmItineraryLoading] =
    React.useState(false);
  const [contacts, setContacts] = React.useState(props.allContacts);
  const [createNewContactDrawerVisible, setCreateNewContactDrawerVisible] =
    React.useState(false);
  const [createNewItineraryDrawerVisible, setCreateNewItineraryDrawerVisible] =
    React.useState(false);
  const [editItineraryDrawerVisible, setEditItineraryDrawerVisible] =
    React.useState(false);
  const [selectedItineraries, setSelectedItineraries] = React.useState([]);
  const [selectedContact, setSelectedContact] = React.useState(undefined);
  const [itineraries, setItineraries] = React.useState(
    props.budget?.itineraries || [],
  );
  const [activeTemplateCode, setActiveTemplateCode] = React.useState(undefined);
  const [selectedClientId, setSelectedClientId] = React.useState(
    props.budget?.client || context?.clientId,
  );
  const clientSelectRef = React.useRef();

  const { data: clientTemplateData } = useGetClientTemplateQuery({
    variables: {
      id: selectedClientId,
    },
    skip: !selectedClientId,
    fetchPolicy: 'cache-and-network',
  });

  const clientTemplate = clientTemplateData?.client?.template;

  const createContactMutation = useCreateContactMutation({
    client: getClient(),
  });
  React.useEffect(() => {
    if (
      (props.budget?.itineraries || []).find((it) => {
        const matchingIt = itineraries.find((iti) => iti.id === it.id);

        if (matchingIt) {
          return matchingIt.realized !== it.realized;
        }
        return false;
      })
    ) {
      setConfirmItineraryLoading(false);
      setItineraries(props.budget?.itineraries || []);
    }
  }, [props.budget?.itineraries]);

  React.useEffect(() => {
    if (clientSelectRef.current) {
      // @ts-ignore
      clientSelectRef.current?.focus?.();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    props.form.validateFields(async (err, values: ICreateBudgetFormValues) => {
      if (!err) {
        const sanitizedValues = sanitizeObject<ICreateBudgetFormValues>(values);
        const budgetCandidate: BudgetCandidate = {
          client: selectedClientId,
          ...sanitizedValues,
          contactId: sanitizedValues.contact,
          creationDate: sanitizedValues.creationDate,
          itineraries: itineraries.map((i) => {
            const { _notSubmitted, ...itinerary } = i;
            return itinerary;
          }),
        };

        try {
          message.loading(
            {
              content: props.budget
                ? 'Guardando los cambios..'
                : 'Creando presupuesto',
              key: 'budget_create',
            },
            0,
          );
          await props.onSubmit(budgetCandidate);
          message.success(
            {
              content: 'Hecho!',
              key: 'budget_create',
            },
            0,
          );
          // this.props.close();
        } catch (e) {
          message.error(
            {
              content: 'Error al realizar la operación',
              key: 'budget_create',
            },
            0,
          );

          console.error(e);
        } finally {
          setTimeout(() => {
            message.destroy();
          }, 2000);
        }
      } else {
        console.error('error on form validation', err);
      }
    });
  };

  const openCreateContactDrawer = () => {
    setCreateNewContactDrawerVisible(true);
  };

  const hideCreateContactDrawer = () => {
    setCreateNewContactDrawerVisible(false);
  };

  const openCreateItineraryDrawer = () => {
    setCreateNewItineraryDrawerVisible(true);
  };

  const hideCreateItineraryDrawer = () => {
    setCreateNewItineraryDrawerVisible(false);
  };

  const openEditItineraryDrawer = () => {
    setEditItineraryDrawerVisible(true);
  };

  const hideEditItineraryDrawer = () => {
    setEditItineraryDrawerVisible(false);
  };

  const removeItinerary = () => {
    const newItineraries = (itineraries as ItineraryCandidate[]).filter(
      (itinerary) => !selectedItineraries.includes(itinerary.id),
    );

    setItineraries(newItineraries);

    cleanRowSelection();
  };

  const confirmItinerary = () => {
    const itineraryToConfirmId = selectedItineraries[0];
    setConfirmItineraryLoading(true);
  };

  React.useEffect(() => {
    if (confirmItineraryLoading) {
      props.onConfirmItinerary(selectedItineraries[0]);
    }
  }, [confirmItineraryLoading]);

  const cleanRowSelection = () => {
    setSelectedItineraries([]);
  };

  const duplicateItinerary = () => {
    const itinerariesToDuplicate = (itineraries as ItineraryCandidate[]).filter(
      (itinerary) => selectedItineraries.includes(itinerary.id),
    );

    const itinerariesToDuplicateWithComputedId = itinerariesToDuplicate.map(
      (itToDuplicate) => {
        return {
          ...itToDuplicate,
          realized: false,
          id: UUID.v4(),
          _notSubmitted: true,
        };
      },
    );

    setItineraries([...itinerariesToDuplicateWithComputedId, ...itineraries]);

    cleanRowSelection();
  };

  const addItinerary = (itinerary: ItineraryCandidate) => {
    setItineraries([
      {
        ...itinerary,
        _notSubmitted: true,
      },
      ...itineraries,
    ]);
  };

  const editItinerary = (itinerary: ItineraryCandidate) => {
    const editedArray = [];

    itineraries.forEach((_itinerary) => {
      if (_itinerary.id !== itinerary.id) {
        editedArray.push(_itinerary);
      } else {
        editedArray.push(itinerary);
      }
    });

    setItineraries(editedArray);
  };

  const onItinerarySelectChange = (selectedRowKeys: string[]) => {
    setSelectedItineraries(selectedRowKeys);
  };

  const onCreateContact = async (values: ICreateContactFormValues) => {
    const result = await createContactMutation[0]({
      variables: {
        email: values.email,
        name: values.name,
        phone: values.phone,
      },
      update: (cache, result) => {
        cache.updateQuery<ContactsQuery, ContactsQueryVariables>(
          {
            query: ContactsDocument,
          },
          (data) => {
            return {
              ...data,
              contacts: [...data.contacts, result.data.createContact],
            };
          },
        );
      },
    });

    if (result.data?.createContact?.id) {
      setContacts([...contacts, result.data.createContact]);
      setSelectedContact(result.data?.createContact?.id);
    }
    hideCreateContactDrawer();
  };

  const { getFieldDecorator } = props.form;
  const itinerariesRowSelection = {
    selectedRowKeys: selectedItineraries,
    onChange: onItinerarySelectChange,
  };

  const activeTemplate = props.budgetConfig?.templates.find(
    (template) => template.code === activeTemplateCode,
  );

  const selectedItineraryToEdit = itineraries.find(
    (itinerary) => itinerary.id === selectedItineraries[0],
  );

  const itineraryColumns = [
    {
      title: 'Fecha',
      dataIndex: 'date',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return formatDate(itinerary.departureTime);
      },
    },
    {
      title: 'Hora',
      dataIndex: 'time',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return formatTime(itinerary.departureTime);
      },
    },
    {
      title: 'Estado',
      render: (_: any, itinerary: ItineraryCandidate) => {
        if (itinerary.realized) {
          return <Tag color={'green'}>{'Confirmado'}</Tag>;
        } else {
          return <Tag color={'volcano'}>{'Sin confirmar'}</Tag>;
        }
      },
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      ellipsis: true,
      render: (_: any, itinerary: ItineraryCandidate) => {
        return itinerary.itineraryDescription;
      },
    },
    {
      title: 'Referencia',
      dataIndex: 'reference',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return itinerary.reference;
      },
    },
    {
      title: 'Vehículos',
      dataIndex: 'vehicles',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return itinerary.numVehicles || 0;
      },
    },
    {
      title: '# Pax',
      dataIndex: 'numPassengers',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return itinerary.numPassengers || 0;
      },
    },
    {
      title: 'Total (con I.V.A)',
      dataIndex: 'total',
      render: (_: any, itinerary: ItineraryCandidate) => {
        return `${computeItineraryTotalWithVAT(itinerary).toFixed(2)} €`;
      },
    },
  ];

  return (
    <>
      <Form onSubmit={handleSubmit} layout={'vertical'}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={12}>
            <Form.Item label={'Fecha de alta'} required>
              {getFieldDecorator('creationDate', {
                initialValue: props.budget?.creationDate || moment(),
                rules: [
                  {
                    required: true,
                    message: 'Por favor, establezca una fecha de alta',
                  },
                ],
              })(<DatePicker format={'DD/MM/YYYY'} />)}
            </Form.Item>
            {context.isAdmin && (
              <Form.Item label={'Atendido por'}>
                <Input disabled={true} value={context.name} />
              </Form.Item>
            )}
            <Form.Item label={'Empresa'} required>
              {getFieldDecorator('company', {
                initialValue:
                  props.budget?.company ||
                  props.budgetConfig?.companies?.[0]?.code,
                rules: [
                  {
                    required: true,
                    message: 'Por favor, selecciona una empresa',
                  },
                ],
              })(
                <Select allowClear>
                  {props.budgetConfig?.companies.map((company) => {
                    return (
                      <Select.Option key={company.code} value={company.code}>
                        {company.data.name}
                      </Select.Option>
                    );
                  })}
                </Select>,
              )}
            </Form.Item>
            {context.isAdmin && (
              <Form.Item label={'Cliente'} required>
                {getFieldDecorator('client', {
                  initialValue: props.budget?.client,
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, selecciona un cliente',
                    },
                  ],
                })(
                  <Select
                    ref={clientSelectRef}
                    showSearch
                    placeholder={'Busca por nombre, NIF, etc'}
                    allowClear
                    autoFocus={true}
                    notFoundContent={
                      'No se ha encontrado ningún cliente que incluya esa información'
                    }
                    onDropdownVisibleChange={(open) => {
                      if (open) {
                        props.refetchClients();
                      }
                    }}
                    onSelect={(value) => {
                      setSelectedClientId(value);
                    }}
                    onDeselect={(value) => {
                      setSelectedClientId(undefined);
                    }}
                    filterOption={(inputValue, option) => {
                      const clientId = option.props.value;
                      const client: Client = props.allClients.find(
                        (client) => client.id === clientId,
                      );

                      return clientSearchFunction(inputValue, client);
                    }}
                  >
                    {props.allClients.map((client) => {
                      return (
                        <Select.Option key={client.id} value={client.id}>
                          {client.name}
                          <span
                            style={{
                              position: 'absolute',
                              color: '#999',
                              right: '32px',
                            }}
                          >
                            {client.nif}
                          </span>
                        </Select.Option>
                      );
                    })}
                  </Select>,
                )}
              </Form.Item>
            )}
            <Divider />
            {context.isAdmin && (
              <>
                <Form.Item label={'A la atención de (Contacto)'}>
                  <Form.Item>
                    {getFieldDecorator('contact', {
                      initialValue:
                        selectedContact || props.budget?.contact?.id,
                      rules: [
                        {
                          required: false,
                          message: 'Por favor, selecciona un contacto',
                        },
                      ],
                    })(
                      <Select
                        // defaultValue={this.state.selectedContact}
                        showSearch
                        placeholder={'Busca por nombre'}
                        allowClear
                        onDropdownVisibleChange={(open) => {
                          if (open) {
                            props.refetchContacts();
                          }
                        }}
                        notFoundContent={
                          'No se ha encontrado ningún contacto que incluya esa información'
                        }
                      >
                        {(contacts as TemporaryContact[]).map((contact) => {
                          return (
                            <Select.Option key={contact.id} value={contact.id}>
                              {contact.name}
                              <span
                                style={{
                                  position: 'absolute',
                                  color: '#999',
                                  right: '32px',
                                }}
                              >
                                {contact.email}
                              </span>
                            </Select.Option>
                          );
                        })}
                      </Select>,
                    )}
                  </Form.Item>
                  <Form.Item
                    label={'También puedes crear uno nuevo'}
                    style={{ marginBottom: 0, paddingBottom: 0 }}
                  >
                    <Button onClick={openCreateContactDrawer}>
                      Crear contacto
                    </Button>
                  </Form.Item>
                </Form.Item>
              </>
            )}
          </Col>
          <Col span={12}>
            <Form.Item label={'Referencia'}>
              {getFieldDecorator('reference', {
                initialValue: props.budget?.reference,
                rules: [
                  {
                    whitespace: true,
                    message:
                      'La referencia no puede contener únicamente carácteres en blanco',
                  },
                ],
              })(<Input placeholder="Eg: X3234J" />)}
            </Form.Item>

            <Form.Item label={'Estado'} required>
              {getFieldDecorator('status', {
                initialValue: props.budget?.status || 'work-in-progress',
                rules: [
                  {
                    required: true,
                    message: 'Por favor, selecciona un estado',
                  },
                ],
              })(
                <Select allowClear>
                  {props.budgetConfig?.status.map((budgetStatus) => {
                    return (
                      <Select.Option
                        key={budgetStatus.code}
                        value={budgetStatus.code}
                      >
                        {capitalizeFirstChar(
                          budgetStatus.locale.find(
                            (locale) => locale.code === 'esES',
                          )?.value,
                        )}
                      </Select.Option>
                    );
                  })}
                </Select>,
              )}
            </Form.Item>

            <Form.Item label="Periodo de validez" required>
              {getFieldDecorator('validityPeriod', {
                initialValue: props.budget?.validityPeriod || [
                  moment(),
                  moment().endOf('year'),
                ],
                rules: [
                  {
                    required: true,
                    message: 'Por favor, introduce una periodo de validez',
                  },
                ],
              })(<DatePicker.RangePicker format={'DD/MM/YYYY'} />)}
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={24}>
            <Tabs type="card">
              <Tabs.TabPane tab="Itinerarios" key="itineraries">
                {!selectedClientId && (
                  <Alert
                    style={{ marginBottom: '2em' }}
                    closable={true}
                    type={'warning'}
                    message={
                      'Para poder crear un nuevo itinerario, necesitas primero seleccionar un cliente'
                    }
                  />
                )}
                <Form.Item>
                  <div
                    style={{
                      flex: 1,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      marginBottom: 20,
                      display: 'flex',
                    }}
                  >
                    <div style={{ flex: 1, display: 'flex' }}>
                      {props.budget && (
                        <Button
                          loading={confirmItineraryLoading}
                          disabled={
                            !(
                              selectedItineraries.length === 1 &&
                              props.budget?.itineraries.find(
                                (i) => i.id === selectedItineraries[0],
                              )?.realized === false &&
                              !props.budget?.itineraries.find(
                                (i) => i.id === selectedItineraries[0],
                              )?._notSubmitted
                            )
                          }
                          type={'ghost'}
                          icon={<LegacyIcon type={'check'} />}
                          onClick={() => confirmItinerary()}
                        >
                          Confirmar
                        </Button>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        disabled={selectedItineraries.length === 0}
                        danger
                        icon={<LegacyIcon type={'close'} />}
                        onClick={() => removeItinerary()}
                      >
                        Eliminar
                      </Button>
                      <Button
                        disabled={selectedItineraries.length === 0}
                        type={'ghost'}
                        icon={<LegacyIcon type={'copy'} />}
                        style={{ marginLeft: 10 }}
                        onClick={() => duplicateItinerary()}
                      >
                        Duplicar
                      </Button>

                      <Button
                        onClick={() => openEditItineraryDrawer()}
                        disabled={selectedItineraries.length !== 1}
                        style={{ marginLeft: 10 }}
                        icon={<LegacyIcon type={'edit'} />}
                      >
                        Modificar
                      </Button>
                      <Button
                        disabled={!selectedClientId}
                        onClick={openCreateItineraryDrawer}
                        style={{ marginLeft: 10 }}
                        icon={<LegacyIcon type={'plus'} />}
                        type={'primary'}
                      >
                        Nuevo
                      </Button>
                    </div>
                  </div>

                  <Table
                    onRow={(record, index) => {
                      return {
                        onClick: (e) => {
                          e.preventDefault();

                          if (selectedItineraries.includes(record.id)) {
                            setSelectedItineraries(
                              selectedItineraries.filter(
                                (si) => si !== record.id,
                              ),
                            );
                          } else {
                            setSelectedItineraries([record.id]);
                          }
                        },
                      };
                    }}
                    size="small"
                    rowClassName={(record) => {
                      if (record._notSubmitted) {
                        return 'blink-1';
                      }
                    }}
                    pagination={{ pageSize: 5 }}
                    rowKey={(record: ItineraryCandidate) => record.id}
                    bordered={false}
                    rowSelection={itinerariesRowSelection}
                    columns={itineraryColumns}
                    dataSource={itineraries}
                    locale={{
                      emptyText: (
                        <Empty
                          description="No hay itinerarios todavía"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ),
                    }}
                  />
                </Form.Item>

                <Row>
                  <Col span={24}>
                    <Form.Item>
                      <Descriptions bordered={true} size="small" column={1}>
                        <Descriptions.Item label={'Suma de importes'}>
                          {itineraries
                            .reduce(
                              (
                                total: number,
                                currentValue: ItineraryCandidate,
                              ) => {
                                return (
                                  total +
                                  computeItineraryTotalWithoutVAT(currentValue)
                                );
                              },
                              0,
                            )
                            .toFixed(2)}
                          {' €'}
                        </Descriptions.Item>
                        <Descriptions.Item label={'Base imponible'}>
                          {itineraries
                            .reduce(
                              (
                                total: number,
                                currentValue: ItineraryCandidate,
                              ) => {
                                return (
                                  total +
                                  computeItineraryTotalWithoutVAT(currentValue)
                                );
                              },
                              0,
                            )
                            .toFixed(2)}
                          {' €'}
                        </Descriptions.Item>
                        <Descriptions.Item label={'Importe I.V.A'}>
                          {itineraries
                            .reduce(
                              (
                                total: number,
                                currentValue: ItineraryCandidate,
                              ) => {
                                return (
                                  total +
                                  computeItineraryVATSumatory(currentValue)
                                );
                              },
                              0,
                            )
                            .toFixed(2)}
                          {' €'}
                        </Descriptions.Item>
                        <Descriptions.Item label={'Total (Con I.V.A)'}>
                          {itineraries
                            .reduce(
                              (
                                total: number,
                                currentValue: ItineraryCandidate,
                              ) => {
                                return (
                                  total +
                                  computeItineraryTotalWithVAT(currentValue)
                                );
                              },
                              0,
                            )
                            .toFixed(2)}
                          {' €'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              {context.isAdmin && (
                <Tabs.TabPane
                  key={clientTemplate?.id}
                  tab="Plantillas"
                  tabKey="templates"
                >
                  {!clientTemplate && (
                    <p>
                      El cliente seleccionado no tiene una plantilla asignada.
                      Puedes crear una nueva plantilla para este cliente desde
                      la pestaña de clientes.
                    </p>
                  )}
                  {clientTemplate && (
                    <>
                      <Form.Item label={'Cabecera'}>
                        <BasicHTMLEditor
                          editable={false}
                          value={clientTemplate?.heading as JSONContent}
                        />
                      </Form.Item>
                      <Form.Item label={'Pie'}>
                        <BasicHTMLEditor
                          editable={false}
                          value={clientTemplate?.body as JSONContent}
                        />
                      </Form.Item>
                    </>
                  )}
                </Tabs.TabPane>
              )}
            </Tabs>
          </Col>
        </Row>
        <Divider />

        <StickyFooterFormButtons>
          <Button
            size={'large'}
            style={{ float: 'left' }}
            danger
            onClick={props.close}
          >
            {'Cerrar sin guardar'}
          </Button>
          <Button
            size={'large'}
            style={{ float: 'right' }}
            type="primary"
            htmlType="submit"
          >
            {props.budget ? 'Guardar y cerrar' : 'Crear presupuesto'}
          </Button>
        </StickyFooterFormButtons>
      </Form>
      <CreateContact
        onClose={hideCreateContactDrawer}
        visible={createNewContactDrawerVisible}
        onSubmit={onCreateContact}
      />
      <CreateItinerary
        clientId={selectedClientId}
        allVehicleTypes={props.allVehicleTypes}
        budgetConfig={props.budgetConfig}
        vatTypes={[...(props.budgetConfig?.vatTypes || [])]}
        onSubmit={(
          itinerary: Partial<Pick<ItineraryCandidate, 'id'>> &
            Omit<ItineraryCandidate, 'id'>,
        ) => {
          const itineraryToAdd: ItineraryCandidate = {
            ...itinerary,
            id: UUID.v4(),
          };

          addItinerary(itineraryToAdd);
          hideCreateItineraryDrawer();
        }}
        visible={createNewItineraryDrawerVisible}
        onClose={hideCreateItineraryDrawer}
      />
      {selectedItineraryToEdit && (
        <EditItinerary
          clientId={selectedClientId}
          allVehicleTypes={props.allVehicleTypes}
          budgetConfig={props.budgetConfig}
          vatTypes={[...(props.budgetConfig?.vatTypes || [])]}
          itinerary={selectedItineraryToEdit}
          visible={editItineraryDrawerVisible}
          onClose={hideEditItineraryDrawer}
          onSubmit={(
            itinerary: Partial<Pick<ItineraryCandidate, 'id'>> &
              Omit<ItineraryCandidate, 'id'>,
          ) => {
            const previousState = props.budget?.itineraries.find(
              (i) => i.id === selectedItineraries?.[0],
            );

            const itineraryToEdit: ItineraryCandidate = {
              ...itinerary,
              realized: previousState?.realized,
              id: selectedItineraries?.[0],
            };

            editItinerary(itineraryToEdit);
            hideEditItineraryDrawer();
          }}
        />
      )}
    </>
  );
};

export const MyCreateBudgetForm =
  Form.create<ICreateBudgetFormProps>()(CreateBudgetForm);
